/* eslint-disable @typescript-eslint/no-explicit-any */
import { useSelector } from 'react-redux';
import { Command, State } from '@store/index';
import { FullEdge, Translation, TranslationNode } from '../../components/Flow/types';
import { getCommandsWithDynamicAnchors, getFullEdges } from '@common/utils/flow';
import { useNodeData } from '@common/hooks/useNodeData';
import { NodeCondition, NodeField, NodeInputType, config } from '../../components/Flow/Nodes';
import { getVariableOptions } from '@common/Expression/queryLanguage';

type RecursiveArray = Array<RecursiveArray | string> | null;

const dynamicVariableRegex = /{\{\s*(.*?)\s*\}\}/g;

export const useTranslate = () => {
  const tabs = useSelector((state: State) => state.tabs.list);

  const {
    getDataConnections,
    getPluginDataConnections,
    getWebhookId,
    getNodeOutput,
    getInputDataConnections,
    getCommand,
  } = useNodeData();

  const translate = (workflowId: string): Translation => {
    const tab = tabs.find((tab) => tab.flow_id === workflowId);

    const edges = tab?.edges || [];
    const name = tab?.label || '';
    const id = tab?.flow_id || '';

    const commands = getCommandsWithDynamicAnchors(tab?.commands || []);
    const fullEdges = getFullEdges(edges, commands);
    const nodesWithoutPlugins = translateNodes(commands, fullEdges, workflowId);
    const nodes = getNodesWithPlugins(commands, nodesWithoutPlugins, workflowId);

    const translation: Translation = {
      name,
      flow_id: id,
      flow: tab?.flow,
      edges: fullEdges.map((item) => ({
        source: item.edge.source,
        target: item.edge.target,
        sourceHandle: item.edge.sourceHandle as string,
        targetHandle: item.edge.targetHandle as string,
        variant: item.source_anchor.variant,
      })),
      nodes,
    };

    return translation;
  };

  const translateNodes = (commands: Command[], full_edges: FullEdge[], workflowId: string) => {
    const trigger = commands.find((command: Command) => command.config?.trigger);
    if (!trigger?.node.id) return [];

    const tree_nodes = getTreeNodes(full_edges, trigger.node.id, commands);
    if (!tree_nodes) return [];

    return tree_nodes.map((command: Command) => translateNode(command, commands, workflowId));
  };

  const translateNode = (command: Command, commands: Command[], workflowId: string): TranslationNode => {
    const { node, config } = command;

    const attributes = command?.fields?.map((field) => createAttributeObject(field, command, commands, workflowId));
    const output = JSON.stringify(command.output);
    const variables = attributes.map((attr) => attr.variables).flat(100);

    // console.log('node', node.id, attributes, variables);

    return {
      id: config.command_id as string,
      type: node.type as string,
      trigger: config?.trigger || false,
      node_id: node.id,
      command_id: config.command_id,
      position: node.position,
      width: node.width,
      attributes: JSON.stringify(attributes),
      output,
      version: config.version,
      variables: Array.from(new Set(variables)),
      webhooks: JSON.stringify(config?.webhooks),
    };
  };

  const getNodesWithPlugins = (commands: Command[], executions: TranslationNode[], workflowId: string) => {
    const plugins = commands.filter(
      (command: Command) => !executions.some((execution) => execution.node_id === command.node.id)
    );

    const enriched_data_nodes: TranslationNode[] = plugins.map((command: Command) => {
      const { node, config, fields, state } = command;

      const attributes = fields.map((field) => {
        const value = state[field.id] || null;

        const dataInputs = getPluginDataConnections(command, true);
        const { value: full_value, variables } = processDynamicVariables(
          field.id,
          value,
          dataInputs,
          [],
          commands,
          workflowId
        );

        /**
         * The value is the raw field value
         * The full_value is the raw field value with all dynamic variables prepended with their node id
         */

        return { ...field, value, full_value, variables };
      });

      const variables = attributes.map((attr) => attr.variables).flat(100);
      // console.log('node', node.id, attributes, variables);
      const output = JSON.stringify(command.output);

      return {
        id: config.command_id as string,
        type: node.type as string,
        trigger: config.trigger || false,
        node_id: node.id,
        command_id: config.command_id,
        position: node.position,
        width: node.width,
        attributes: JSON.stringify(attributes),
        output,
        version: config.version,
        variables: Array.from(new Set(variables)),
      };
    });

    return [...executions, ...enriched_data_nodes];
  };

  const createAttributeObject = (field: NodeField, command: Command, commands: Command[], workflowId: string) => {
    const { state } = command;

    /* all data connections to this node
      this includes the tools attached to an AI completion node as they pass data */
    const dataInputs = getDataConnections(command, true);

    // the value before the node identifier is added
    const value = state[field.id];

    // adds node identifier to every dynamic variable
    const { value: full_value, variables } = processDynamicVariables(
      field.id,
      value,
      dataInputs,
      [],
      commands,
      workflowId
    );

    return { ...field, value, full_value, variables };
  };

  function extractVariableName(jmespath: string): string {
    const regex = /^[a-zA-Z_][a-zA-Z0-9_]*(?:\.[a-zA-Z_][a-zA-Z0-9_]*)*/;
    const match = jmespath.match(regex);
    return match ? match[0] : '';
  }

  const processDynamicVariables = (
    fieldId: string,
    input: any,
    dataInputs: FullEdge[],
    variables: string[] = [],
    commands: Command[],
    workflowId: string
  ): any => {
    const tab = tabs.find((tab) => tab.flow?.flow_id === workflowId);
    const dataPrefixes = tab?.flow?.data_prefixes || {};
    const hasDataPrefixes = Object.keys(dataPrefixes).length > 0;

    if (typeof input === 'string' || typeof input === 'boolean' || typeof input === 'number')
      return { value: input, variables };

    /* this is the base case for this recursive function
      we keep going deeper into the input until we hit a primative */

    if (isNodeInput(input)) {
      const { value, type } = input;

      if (type === NodeInputType.Text || type === NodeInputType.Option || type === NodeInputType.Number)
        return { value: input.value, variables };

      if (type === NodeInputType.Expression) {
        let valueWithNodeIds = value;
        const matches: RegExpMatchArray[] = [...value.matchAll(dynamicVariableRegex)];

        for (const match of matches) {
          const {
            original,
            replacement,
            variables: variablesUsed,
          } = matchReplacements(match, dataInputs, commands, workflowId);

          valueWithNodeIds = valueWithNodeIds.replace(original, replacement);
          variables.push(...variablesUsed);
        }

        return { value: valueWithNodeIds, variables };
      }
    }

    // handles the condition group specifically
    if (Array.isArray(input) && input.some((item) => item.conditions !== undefined)) {
      return {
        value: input.map((conditionGroup) => {
          const updatedConditions = conditionGroup.conditions.map((condition: NodeCondition) => {
            const conditionUpdate = { ...condition };
            const keys = Object.keys(conditionUpdate);

            for (const key of keys) {
              let val = conditionUpdate[key as keyof NodeCondition];

              if (typeof val === 'string' && (dataInputs.length > 0 || hasDataPrefixes)) {
                const matchesArray = [...val.matchAll(dynamicVariableRegex)];

                matchesArray.forEach((match) => {
                  const {
                    original,
                    replacement,
                    variables: variablesUsed,
                  } = matchReplacements(match, dataInputs, commands, workflowId);

                  val = val.replace(original, replacement);
                  variables.push(...variablesUsed);
                });

                conditionUpdate[key as keyof NodeCondition] = val;
              }
            }

            return conditionUpdate;
          });

          return { ...conditionGroup, conditions: updatedConditions };
        }),
        variables,
      };
    }

    // used for CustomHeaders and MediaUrls currently
    if (isObjectExpressionArray(input)) {
      const items = input.value.map((item: { id: string; value: string }) => {
        const itemValue = item.value;
        const itemInput = {
          type: NodeInputType.Expression,
          value: itemValue,
        };

        const { value: val } = processDynamicVariables(fieldId, itemInput, dataInputs, variables, commands, workflowId);
        return { ...item, value: val };
      });

      return { value: items, variables };
    }

    if (isObjectArray(input)) {
      return {
        value: input.map((item: any) => {
          const keys = Object.keys(item);
          const updatedItem = { ...item };

          for (const key of keys) {
            const { value: val } = processDynamicVariables(
              fieldId,
              item[key],
              dataInputs,
              variables,
              commands,
              workflowId
            );
            updatedItem[key] = val;
          }

          return updatedItem;
        }),
        variables,
      };
    }

    if (isCard(input)) {
      const updatedCard = { ...input };

      Object.entries(updatedCard).forEach(([key, value]) => {
        const { value: val } = processDynamicVariables(fieldId, value, dataInputs, variables, commands, workflowId);
        updatedCard[key] = val;
      });

      return { value: updatedCard, variables };
    }

    // handles the single Branch condition
    if (isObject(input) && 'operator' in input) {
      const keys = Object.keys(input);

      for (const key of keys) {
        const val = input[key];

        if (typeof val === 'string' && (dataInputs.length > 0 || hasDataPrefixes)) {
          const matches = val.matchAll(dynamicVariableRegex);

          for (const match of matches) {
            const {
              original,
              replacement,
              variables: variablesUsed,
            } = matchReplacements(match, dataInputs, commands, workflowId);

            input = { ...input, [key]: val.replace(original, replacement) };
            variables.push(...variablesUsed);
          }
        }
      }

      return { value: input, variables };
    }

    // handles the replacements of the JumpTo node
    if (isObject(input) && fieldId === 'replacements') {
      const keys = Object.keys(input);

      for (const key of keys) {
        const val = input[key];

        if (typeof val === 'string' && (dataInputs.length > 0 || hasDataPrefixes)) {
          const matches = val.matchAll(dynamicVariableRegex);

          for (const match of matches) {
            const {
              original,
              replacement,
              variables: variablesUsed,
            } = matchReplacements(match, dataInputs, commands, workflowId);

            input = { ...input, [key]: val.replace(original, replacement) };
            variables.push(...variablesUsed);
          }
        }
      }

      return { value: input, variables };
    }

    if (Array.isArray(input)) {
      if (input.length === 0) return { value: [], variables };
      return { value: input, variables };
    }

    if (isObject(input)) {
      const valueWithNodeIds: { [key: string]: string } = {};

      Object.entries(input).forEach(([key, value]) => {
        const { value: val } = processDynamicVariables(fieldId, value, dataInputs, variables, commands, workflowId);
        valueWithNodeIds[key] = val;
      });

      return { value: valueWithNodeIds, variables };
    }

    return { value: input, variables };
  };

  /**
   * This function is a helper function to be used by the node anchors
   * It checks if a node is connecting to a previous node that would cause an infinite loop
   * @param source The source node id of the edge
   * @param target The target node id of the edge
   * @param commands A list of all commands to find the starting node
   * @param full_edges A list of all edges to find the possible branches of the flow
   * @returns A boolean represeting whether connecting and edge from these two nodes would cause an infinite loop
   */

  const isInfiniteLoop = (source: string, target: string, commands: Command[], full_edges: FullEdge[]) => {
    const branches = branchLength(full_edges, target);

    if (!Array.isArray(branches) || branches?.length === 0) return false;

    const trigger = commands.find((command: Command) => command.config?.trigger);

    for (const _branch of branches) {
      const branch = trigger ? [trigger?.node?.id, ...[_branch].flat(10)] : [_branch].flat(10);
      if (source === trigger?.node.id) return false;
      if (branch.includes(source) && branch.includes(target) && branch.length > 2) return true;
    }

    return false;
  };

  const getChildrenNodes = (full_edges: FullEdge[], node_id: string): string[] => {
    const edges = full_edges.filter((item) => {
      const { source_anchor, target_anchor } = item;

      const hasTarget = item.edge.source === node_id;
      const hasExecutionTarget =
        source_anchor?.variant.includes('execution') || target_anchor?.variant.includes('execution');

      // if the node has a target and the target is an execution line, return the edge
      if (hasTarget && hasExecutionTarget) return item;
      return null;
    });

    // filter any edges that dont have a target
    return edges.map((item) => item.edge.target);
  };

  const branchLength = (full_edges: FullEdge[], node_id: string, checked: string[] = [node_id]): RecursiveArray => {
    const children = getChildrenNodes(full_edges, node_id);

    if (!full_edges.some((item) => item.edge.source === node_id)) return checked;

    if (children.length > 0) {
      return children.map((child) => branchLength(full_edges, child, [...checked, child]));
    }

    return checked;
  };

  const getTreeNodes = (full_edges: FullEdge[], node_id: string, commands: Command[]): Command[] => {
    const tree_node_ids = getTreeNodeIdentifiers(full_edges, node_id);
    const nodes = tree_node_ids.map((id: string) => commands.find((command) => command.node.id === id));
    return nodes as Command[];
  };

  const getTreeNodeIdentifiers = (full_edges: FullEdge[], node_id?: string): string[] => {
    if (!node_id) return [];
    const nodesReached = branchLength(full_edges, node_id);

    if (Array.isArray(nodesReached)) {
      // @ts-expect-error The array will never be deep enough for this error to be a concern.
      return [...new Set(nodesReached.flat(1000))];
    }

    return [];
  };

  const matchReplacements = (
    match: RegExpMatchArray,
    dataInputs: FullEdge[],
    commands: Command[],
    workflowId: string
  ) => {
    const variables: string[] = [];
    const tab = tabs.find((tab) => tab.flow_id === workflowId);

    const [replaceInput, expressionContent] = match;

    let valueWithNodeIds = replaceInput;
    let processedExpression = expressionContent;

    const dataPrefixes = tab?.flow?.data_prefixes || {};
    const dataPrefixesPattern = Object.values(dataPrefixes).join('|').replace(/\./g, '\\.');
    const dataPrefixesRegex = new RegExp(`\\b(${dataPrefixesPattern})\\.[\\w\\.]+\\b`, 'g');
    const dataPrefixesMatches = [...expressionContent.matchAll(dataPrefixesRegex)];

    // Process and replace data prefix with node ID
    for (const dataPrefixMatch of dataPrefixesMatches) {
      const varName = dataPrefixMatch[0];
      const prefix = dataPrefixMatch[1];

      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const prefixNodeId = Object.entries(dataPrefixes).find(([_, dataPrefix]) => dataPrefix === prefix)?.[0];

      if (prefix && prefixNodeId) {
        const updatedVarName = varName.replace(prefix, `node_${prefixNodeId}`);
        processedExpression = processedExpression.replace(varName, updatedVarName);
        variables.push(updatedVarName);
      }
    }

    // Create a regex pattern matching any variable starting with commandIds
    const commandIdPattern = Object.values(config)
      .map((value) => value.config.command_id)
      .join('|')
      .replace(/\./g, '\\.');

    // Match all command variables in the expression
    const varRegex = new RegExp(`\\b(${commandIdPattern})(?:\\.[\\w\\.]+)?\\b`, 'g');
    const varMatches = [...expressionContent.matchAll(varRegex)];

    // Process and add the node id to the variable
    for (const varMatch of varMatches) {
      const varName = varMatch[0];
      const varNodeId = getVariableNodeId(varName, dataInputs, commands, workflowId);

      if (typeof varNodeId === 'string') {
        const updatedVarName = `node_${varNodeId}.${extractVariableName(varName)}`;
        processedExpression = processedExpression.replace(varName, updatedVarName);
        variables.push(updatedVarName);
      }
    }

    valueWithNodeIds = valueWithNodeIds.replace(replaceInput, `{{ ${processedExpression} }}`);

    return { original: replaceInput, replacement: valueWithNodeIds, variables };
  };

  const getVariableNodeId = (varName: string, dataInputs: FullEdge[], commands: Command[], workflowId: string) => {
    const nodeRegex = /^node_\d+/;

    // check if variable already has the node_id
    if (nodeRegex.test(varName)) return null;

    const tab = tabs.find((tab) => tab.flow_id === workflowId);

    const inputs = dataInputs
      .map((input) => {
        const sourceCommand = commands.find((command) => command.node.id === input.edge.source);
        const sourceAnchor = input.source_anchor;
        const commandModule = tab?.modules?.find((mod) => mod?.flow?.flow_id === sourceCommand?.state.moduleId);

        /** @TODO refactor this to work better */
        if (
          sourceCommand?.config?.command_id === 'logic.switch' ||
          sourceCommand?.config?.command_id === 'logic.branch'
        ) {
          const logicInputs = dataInputs
            .map((logicInput) => {
              const command = commands.find((command) => command.node.id === logicInput.edge.target);
              const source = getInputDataConnections(command?.node?.id || '');

              const commandModule = tab?.modules?.find((mod) => mod?.flow?.flow_id === sourceCommand?.state.moduleId);

              const val = source.find((item) => {
                const cmd = getCommand(item.edge.source);
                const command_id = cmd?.config?.command_id || '';

                if (commandModule) {
                  const moduleInputData = getNodeOutput(cmd, item.source_anchor);
                  const varOptions = getVariableOptions(moduleInputData);
                  if (varOptions.includes(varName)) return true;
                }

                const sourceAnchor = item.source_anchor;
                const webhookId = getWebhookId(cmd, sourceAnchor);
                if (webhookId) return true;

                return varName.startsWith(command_id);
              });

              if (val) return val;
              return null;
            })
            .filter((x) => x);

          if (logicInputs.length > 0) return logicInputs[0]?.edge.source;
          return null;
        }

        if (commandModule && sourceCommand) {
          const moduleInputData = getNodeOutput(sourceCommand, sourceAnchor);
          const varOptions = getVariableOptions(moduleInputData);
          if (varOptions.includes(varName)) return sourceCommand?.node?.id;
        }

        const webhookId = sourceCommand ? getWebhookId(sourceCommand, sourceAnchor) : undefined;
        if (webhookId && (webhookId.startsWith(varName) || varName.startsWith(webhookId))) {
          return sourceCommand?.node?.id;
        }

        const command_id = sourceCommand?.config?.command_id;
        if (command_id && varName.startsWith(command_id)) return sourceCommand?.node?.id;

        return null;
      })
      .filter((x) => x);

    if (inputs.length > 0) return inputs[0];
    return null;
  };

  const isObject = (value: any) => {
    return typeof value === 'object' && value !== null;
  };

  const isNodeInput = (input: any) => {
    const type = input?.type;
    const value = input?.value;

    return typeof value === 'string' && Object.values(NodeInputType).includes(type);
  };

  const isObjectArray = (input: any) => {
    return Array.isArray(input) && (input.length === 0 || input.some((item) => isObject(item)));
  };

  const isObjectExpressionArray = (input: any) => {
    const isInputObject = isObject(input);
    const type = input?.type;

    const expressionObjectTypes = [NodeInputType.MediaUrl, NodeInputType.CallHeader];
    return isInputObject && type && expressionObjectTypes.includes(type);
  };

  const isCard = (input: any) => {
    return input?.enabled !== undefined;
  };

  return { translate, isInfiniteLoop };
};
